import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type Cell,
  flexRender,
  getCoreRowModel,
  type Header,
  type HeaderGroup,
  useReactTable,
  type Table as TableType,
  type RowData,
} from "@tanstack/react-table";
import { AlertCircle, ChevronDown, ChevronsUpDown, ChevronUp, GripVertical } from "lucide-react";
import { memo, useMemo, type CSSProperties } from "react";
import { usePrevious } from "react-use";
import { useSpinDelay } from "spin-delay";
import { Skeleton } from "../ui/skeleton";
import { type useTableColumnOrder } from "./useTableColumnOrder";
import { ColumnVisibilityToggle } from "@/components/ColumnVisibilityToggle";
import { type TableProps } from "@/components/DataTable";
import { useTableColumnVisibility } from "@/components/DataTable/useTableColumnVisibility";
import { Tooltip } from "@/components/Tooltip";
import { CELL_HEIGHT, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilterBadges, FilterDrawer } from "@/components/DataTable/Filters/TableFilters";
import { cn } from "@/lib/utils";
import { useSidebar } from "../ui/sidebar";
import { Pagination } from "@/components/ui/pagination";
import { CopyButton } from "./components/CopyButton/CopyButton";
import { EmailDropdown } from "./components/EmailDropdown/EmailDropdown";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    canCopy?: boolean;
    isEmail?: boolean;
    emailTemplate?: {
      getSubject: (row: TData) => string;
      getBody: (row: TData) => string;
    };
  }
}

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    canCopy?: boolean;
  }
}

type Props<TData extends RowData> = Pick<ReturnType<typeof useTableColumnOrder>, "columnOrder" | "onColumnOrderChange"> & TableProps<TData>;

export function TableImplementation<TData>({
  columns,
  data,
  tableId,
  columnOrder,
  onColumnOrderChange,
  filterConfig,
  sortConfig,
  loading,
  errorMessage,
  onRowClick,
  toolbarActions,
  pagination,
}: Props<TData>) {
  const { columnVisibility, onColumnVisibilityChange } = useTableColumnVisibility(tableId);

  const prevData = usePrevious(data);
  const displayData = loading && prevData ? prevData : data;

  const { sidebarWidth } = useSidebar();

  const table = useReactTable<TData>({
    data: displayData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      enableResizing: true,
      enableSorting: false,
    },
    onColumnOrderChange,
    onColumnVisibilityChange,
    onSortingChange: sortConfig?.onSortingChange,
    state: {
      columnVisibility,
      columnOrder,
      sorting: sortConfig?.sorting,
    },
  });

  const { getFlatHeaders } = table;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizing = table.getState().columnSizing;

  const columnSizeVars = useMemo(() => {
    if (columnSizingInfo && columnSizing) {
      const headers = getFlatHeaders();
      const colSizes: { [key: string]: number } = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
    }
  }, [columnSizing, columnSizingInfo, getFlatHeaders]);

  const tableWidth = useMemo(() => {
    return `calc(100vw - ${sidebarWidth})`;
  }, [sidebarWidth]);

  const pageSize = pagination?.pageSize ?? 10;

  return (
    <section>
      <div className="mb-4 flex flex-row items-center justify-between pr-8">
        <div className="flex items-center gap-4">
          {filterConfig && <FilterBadges filters={filterConfig} />}
          {toolbarActions && <div className="flex items-center">{toolbarActions}</div>}
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {filterConfig && <FilterDrawer filters={filterConfig} />}
          <ColumnVisibilityToggle table={table} disabled={loading} />
        </div>
      </div>
      <div className="max-h-[600px] overflow-auto" style={{ width: tableWidth }}>
        <Table style={columnSizeVars}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderContent key={headerGroup.id} headerGroup={headerGroup} columnOrder={columnOrder} />
            ))}
          </TableHeader>
          {table.getState().columnSizingInfo.isResizingColumn ? (
            <MemoizedTableBody
              columnOrder={columnOrder}
              table={table as TableType<unknown>}
              pageSize={pageSize}
              loading={loading}
              errorMessage={errorMessage}
              onRowClick={onRowClick as ((row: unknown) => void) | undefined}
            />
          ) : (
            <TableBodyContent
              columnOrder={columnOrder}
              table={table}
              pageSize={pageSize}
              loading={loading}
              errorMessage={errorMessage}
              onRowClick={onRowClick}
            />
          )}
        </Table>
      </div>
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          pageSizeOptions={pagination.pageSizeOptions}
        />
      )}
    </section>
  );
}

const SKELETON_SIZES = {
  row: `h-[${CELL_HEIGHT}px]`,
  cell: { height: "h-5", width: "w-[80%]" },
} as const;

function TableRowSkeleton({ columns }: { columns: number }) {
  return (
    <TableRow className={SKELETON_SIZES.row} data-testid="table-row-skeleton">
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton className={cn(SKELETON_SIZES.cell.height, SKELETON_SIZES.cell.width)} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function TableBodyLoading({ pageSize, columns }: { pageSize: number; columns: number }) {
  return (
    <TableBody data-testid="table-loading-body">
      {Array.from({ length: pageSize }).map((_, index) => (
        <TableRowSkeleton key={index} columns={columns} />
      ))}
    </TableBody>
  );
}

const TableBodyContent = <TData extends RowData>({
  table,
  columnOrder,
  pageSize,
  loading,
  errorMessage,
  onRowClick,
}: {
  table: TableType<TData>;
  columnOrder: Array<string>;
  pageSize: number;
  loading: boolean;
  errorMessage?: string;
  onRowClick?: (row: TData) => void;
}) => {
  const showSkeletonLoader = useSpinDelay(loading, {
    delay: 300,
    minDuration: 350,
  });

  const isInitialLoad = loading && !table.getRowModel().rows.length;

  if (showSkeletonLoader || isInitialLoad) {
    return <TableBodyLoading pageSize={pageSize} columns={table.getVisibleFlatColumns().length} />;
  }

  if (errorMessage) {
    return (
      <TableBody>
        <TableRow>
          <TableCell
            colSpan={table.getAllColumns().length}
            style={{ height: `${pageSize * CELL_HEIGHT}px` }}
            className="text-center text-error"
          >
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <pre>{errorMessage}</pre>
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const rows = table.getRowModel().rows;

  if (!rows.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={table.getAllColumns().length} style={{ height: `${pageSize * CELL_HEIGHT}px` }} className="text-center">
            No results.
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && "selected"}
          className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        >
          {row.getVisibleCells().map((cell) => (
            <SortableContext key={cell.id} items={columnOrder} strategy={horizontalListSortingStrategy}>
              <DragAlongCell key={cell.id} cell={cell} loading={loading} />
            </SortableContext>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};

const MemoizedTableBody = memo(
  TableBodyContent,
  <TData extends RowData>(
    prev: { table: TableType<TData>; loading: boolean; errorMessage?: string; onRowClick?: (row: TData) => void },
    next: { table: TableType<TData>; loading: boolean; errorMessage?: string; onRowClick?: (row: TData) => void },
  ) => {
    return (
      prev.table.options.data === next.table.options.data &&
      prev.loading === next.loading &&
      prev.errorMessage === next.errorMessage &&
      prev.onRowClick === next.onRowClick
    );
  },
);

function TableHeaderContent<TData>({ headerGroup, columnOrder }: { headerGroup: HeaderGroup<TData>; columnOrder: string[] }) {
  return (
    <TableRow key={headerGroup.id}>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        {headerGroup.headers.map((header) => (
          <DraggableTableHeader key={header.id} header={header} />
        ))}
      </SortableContext>
    </TableRow>
  );
}

const DraggableTableHeader = <TData extends RowData>({ header }: { header: Header<TData, unknown> }) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    whiteSpace: "nowrap",
    width: `calc(var(--header-${header.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableHead
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      className={cn("group relative overflow-hidden")}
      onClick={header.column.getToggleSortingHandler()}
    >
      <Tooltip asChild label="Drag to reorder">
        <button className="absolute left-1 top-3 w-4 opacity-0 group-hover:opacity-100" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4 hover:cursor-grab" />
        </button>
      </Tooltip>
      <div
        className={cn(
          "translate-x-0 transition-transform duration-200 group-hover:translate-x-3",
          header.column.getCanSort() ? "cursor-pointer" : "",
          "line-clamp-2 text-wrap pr-6",
        )}
      >
        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize group-hover:bg-gray-300 ${
            header.column.getIsResizing() ? "bg-gray-500" : ""
          }`}
        />
      )}
      {header.column.getCanSort() && (
        <span className="absolute right-2 top-3">
          {!header.column.getIsSorted() ? (
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          ) : (
            {
              asc: <ChevronUp className="h-4 w-4" />,
              desc: <ChevronDown className="h-4 w-4" />,
            }[header.column.getIsSorted() as string]
          )}
        </span>
      )}
    </TableHead>
  );
};

const DragAlongCell = <TData,>({ cell, loading }: { cell: Cell<TData, unknown>; loading: boolean }) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style: CSSProperties = {
    opacity: loading ? 0.6 : isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : 0,
  };

  const isEmail = cell.column.columnDef.meta?.isEmail;
  const canCopyCell = cell.column.columnDef.meta?.canCopy;
  const emailTemplate = cell.column.columnDef.meta?.emailTemplate;
  const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
  const cellValue = cell.getValue();
  const hasValue = cellValue !== null && cellValue !== undefined && cellValue !== "-" && cellValue !== "";

  return (
    <TableCell style={style} ref={setNodeRef} className={cn(hasValue && "group relative")}>
      <div className="flex items-center justify-between">
        <div className="flex-1 truncate">{cellContent}</div>
        {canCopyCell && hasValue && <CopyButton value={String(cellValue)} />}
        {isEmail && hasValue && (
          <EmailDropdown<TData> email={String(cellValue)} emailTemplate={emailTemplate} rowData={cell.row.original as TData} />
        )}
      </div>
    </TableCell>
  );
};
